import { DateTime } from 'luxon';
import { LocalDate, LocalTime, DateRange } from '../../model/datePicker/types';
import { CellState } from '../../model/datePicker/calendarGrid';

// Solo presentación — nunca se usa para decidir qué día es hoy ni para
// aritmética de rango (eso es Fase 1, fechas locales puras). Locale forzado
// a inglés: la interfaz se entrega en inglés sea cual sea el navegador (D6).
const toDisplay = (d: LocalDate): DateTime => DateTime.fromISO(d).setLocale('en');

// §07 — "Starts D MMM" / "Ends D MMM", año solo si difiere del año del panel.
const formatStripLabel = (kind: 'before' | 'after', date: LocalDate, panelYear: number): string => {
  const dt = toDisplay(date);
  const formatted = dt.year === panelYear ? dt.toFormat('d LLL') : dt.toFormat('d LLL yyyy');
  return kind === 'before' ? `Starts ${formatted}` : `Ends ${formatted}`;
};

// §09 — línea de resumen, cuatro formatos según cómo se solapen mes/año.
const formatRangeReadout = (range: DateRange): string => {
  const start = toDisplay(range.start);
  const end = toDisplay(range.end);
  if(range.start === range.end) {
    return start.toFormat('d LLL yyyy');
  }
  if(start.year === end.year && start.month === end.month) {
    return `${start.toFormat('d')} – ${end.toFormat('d LLL yyyy')}`;
  }
  if(start.year === end.year) {
    return `${start.toFormat('d LLL')} – ${end.toFormat('d LLL yyyy')}`;
  }
  return `${start.toFormat('d LLL yyyy')} – ${end.toFormat('d LLL yyyy')}`;
};

// §09 — sufijo de longitud + corte de día parcial. `days` es la longitud
// compartida (length(B) === length(A), siempre — §04).
const formatFooterSuffix = (days: number, cutoff: LocalTime | null): string => {
  const lengthText = `${days} day${days === 1 ? '' : 's'} each`;
  if(cutoff === null) {
    return `· ${lengthText}`;
  }
  if(cutoff === '00:00') {
    return '· less than an hour into today';
  }
  return `· ${lengthText}, both to ${cutoff}`;
};

// §10 — aria-label por celda: fecha completa + pertenencia, nunca el número
// a secas. outside-month no es interactiva, pero conserva su fecha real.
const formatCellAriaLabel = (day: LocalDate, state: CellState, axis: 'A' | 'B'): string => {
  const date = toDisplay(day).toFormat('d LLLL yyyy');
  const period = `period ${axis}`;
  switch(state) {
    case 'range-start':
      return `${date}, start of ${period}`;
    case 'range-end':
      return `${date}, end of ${period}`;
    case 'range-single':
      return `${date}, ${period}`;
    case 'in-range':
      return `${date}, within ${period}`;
    default:
      return date;
  }
};

export { formatStripLabel, formatRangeReadout, formatFooterSuffix, formatCellAriaLabel };
